import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import LocationDropDowns from '@shared/components/LocationDropDowns';
import { useState, useEffect, useMemo } from 'react';
import { useDocumentsForm } from '@modules/staff/person/forms/personDocuments/hooks/useDocumentsForm';
import { set } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import bg from 'date-fns/locale/bg';
import CustomDatePicker from '@shared/components/CustomDatePicker';

// Register Bulgarian locale
registerLocale('bg', bg);

export default function MainForm({ person, onClose, fetchData }) {
    const { t } = useTranslation();
    const [isBgCitizen, setIsBgCitizen] = useState(true);
    const [isEgn, setIsEgn] = useState(true);
    const [selectedIdentType, setSelectedIdentType] = useState(0);


    const {
        register,
        errors,
        setValue,
        personData,
        countryOptions,
        onSubmit,
        handleIdnChange,
        serverMessage,
        clearErrors,
        watch,
    } = useDocumentsForm(person, t, 'main', fetchData, onClose, isEgn);

    const watchedDateOfBirth = watch('dateOfBirth');
    const dateOfBirthValue = watchedDateOfBirth || personData?.dateOfBirth || person?.dateOfBirth || '';
    const watchedIdentityCardExpDate = watch('identityCardExpDate');
    const identityCardExpDateValue = watchedIdentityCardExpDate || personData?.identityCardExpDate || person?.identityCardExpDate || '';
    const watchedCountry = watch('country');
    const watchedRegion = watch('region');
    const watchedSubRegion = watch('subregion');
    const watchedCity = watch('addressCityId');


    // const [idnOrOther, setIdnOrOther] = useState(person?.idnOrOther || 'idn');
    const [birthplaceCountry, setBirthplaceCountry] = useState('');


    const handleIdnOrOtherChange = (e) => {
        let value = e.target.value;

        // Clear the IDN error when changing identType
        clearErrors('idn');

        switch (value) {
            case "0":
                setSelectedIdentType(0);
                setIsEgn(true);
                setValue('identType', 0);
                break;
            case "1":
                setSelectedIdentType(1);
                setIsEgn(false);
                setValue('identType', 1);
                break;
            case "2":
                setSelectedIdentType(2);
                setIsEgn(false);
                setValue('identType', 2);
                break;
            case "3":
                setSelectedIdentType(3);
                setIsEgn(false);
                setValue('identType', 3);
                break;
        }
    };

    const handleCitizenshipChange = (e) => {
        const value = e.target.value;
        const isBg = value === "true";
        setIsBgCitizen(isBg);
        setValue('bgCitizenship', value);
    };


    useEffect(() => {
        const source = personData || person;

        if (!source) {
            setIsBgCitizen(true);
            setIsEgn(true);
            setSelectedIdentType(0);
            setBirthplaceCountry('');
            setValue('bgCitizenship', 'true');
            setValue('identType', 0);
            setValue('idn', '');
            setValue('dateOfBirth', '');
            setValue('sex', 'female');
            setValue('birthplaceCountry', '');
            return;
        }

        const bgValue = source.bgCitizenship;
        const isBg = bgValue === true || bgValue === 'true';
        setIsBgCitizen(isBg);
        setValue('bgCitizenship', isBg ? 'true' : 'false');

        const identTypeValue = source.identType ?? source.idn?.identType ?? 0;
        setSelectedIdentType(Number(identTypeValue));
        setIsEgn(Number(identTypeValue) === 0);
        setValue('identType', Number(identTypeValue));

        const idnValue = source.idn?.idn ?? (typeof source.idn === 'string' ? source.idn : '');
        setValue('idn', idnValue || '');

        if (source.dateOfBirth) {
            setValue('dateOfBirth', source.dateOfBirth);
        }

        if (source.sex) {
            const normalizedSex = source.sex === 'мъж'
                ? 'male'
                : source.sex === 'жена'
                    ? 'female'
                    : source.sex;
            setValue('sex', normalizedSex);
        }

        const birthCountry = source.birthplaceCountry ?? '';
        setBirthplaceCountry(birthCountry);
        setValue('birthplaceCountry', birthCountry);
    }, [personData, person, setValue]);

    useEffect(() => {
        setValue('addressCountryId', watchedCountry || null);
    }, [watchedCountry, setValue]);

    useEffect(() => {
        setValue('addressRegionId', watchedRegion || null);
    }, [watchedRegion, setValue]);

    useEffect(() => {
        setValue('addressSubRegionId', watchedSubRegion || null);
    }, [watchedSubRegion, setValue]);

    useEffect(() => {
        if (watchedCity) {
            setValue('addressCityId', watchedCity);
        }
    }, [watchedCity, setValue]);

    const handleBirthplaceCountryChange = (selectedOption) => {
        if (selectedOption) {
            setBirthplaceCountry(selectedOption.label);
            setValue('birthplaceCountry', selectedOption.label, { shouldValidate: true });
        }
    };

    const locationFormValues = useMemo(() => ({
        country: watchedCountry || null,
        region: watchedRegion || null,
        subregion: watchedSubRegion || null,
        city: watchedCity || null
    }), [watchedCountry, watchedRegion, watchedSubRegion, watchedCity]);

    return (
        <div className="main-person-form-container">
            <h3 className="form-subtitle">
                {person ? t('persons.form.edit', {
                    name: [person.name, person.fathersName, person.surname]
                        .filter(Boolean)
                        .join(' ')
                }) : t('persons.form.add')}
            </h3>

            <div className="form-content">
                <form onSubmit={onSubmit}>
                    <div className="form-section">
                        {/* Citizenship and IDN Section */}
                        <div className="form-group">
                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.title')}</span>
                                <div className="radio-group">
                                    <div className="radio-item">
                                        <input
                                            type="radio"
                                            value="true"
                                            {...register('bgCitizenship')}
                                            onChange={handleCitizenshipChange}
                                            checked={isBgCitizen === true}
                                        />
                                        <label>{t('persons.form.bgNationality')}</label>
                                    </div>
                                    <div className="radio-item">
                                        <input
                                            type="radio"
                                            value="false"
                                            {...register('bgCitizenship')}
                                            onChange={handleCitizenshipChange}
                                            checked={isBgCitizen === false}
                                        />
                                        <label>{t('persons.form.other')}</label>
                                    </div>
                                </div>
                            </div>

                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.idnOrLnch')}<span className="required">*</span></span>
                                <select
                                    className={`form-control ${errors.idnOrOther ? 'is-invalid' : ''}`}
                                    {...register('identType')}
                                    value={selectedIdentType === 0 ? "0" :
                                        selectedIdentType === 1 ? "1" :
                                            selectedIdentType === 2 ? "2" :
                                                selectedIdentType === 3 ? "3" : "0"}
                                    onChange={handleIdnOrOtherChange}
                                >
                                    {isBgCitizen ? (
                                        <>
                                            <option value="0">{t('persons.form.idnOrLnch-1')}</option>
                                            <option value="1">{t('persons.form.idnOrLnch-2')}</option>
                                            <option value="2">{t('persons.form.idnOrLnch-3')}</option>
                                            <option value="3">{t('persons.form.idnOrLnch-4')}</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value="1">{t('persons.form.idnOrLnch-2')}</option>
                                            <option value="2">{t('persons.form.idnOrLnch-3')}</option>
                                            <option value="3">{t('persons.form.idnOrLnch-4')}</option>
                                        </>
                                    )}
                                </select>
                                {errors.idnOrOther && (
                                    <div className="invalid-feedback d-block">{errors.idnOrOther.message}</div>
                                )}
                            </div>

                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.idn')}<span className="required">*</span></span>
                                <input
                                    type="text"
                                    className={`form-control ${errors.idn ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.idn')}
                                    maxLength={10}
                                    {...register('idn', {
                                        required: selectedIdentType === 0 ? 'IDN is required' : t('validation.person.idn.required'),
                                        pattern: {
                                            value: /^\d{10}$/,
                                            message: t('validation.person.idn.length')
                                        },
                                        onChange: selectedIdentType === 0 ? handleIdnChange : undefined
                                    })}
                                />
                                {errors.idn && (
                                    <div className="invalid-feedback d-block">
                                        {typeof errors.idn === 'string' ? errors.idn : errors.idn.message}
                                    </div>
                                )}
                            </div>
                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.dateOfBirth')}<span className="required">*</span></span>
                                <CustomDatePicker
                                    name="dateOfBirth"
                                    value={dateOfBirthValue}
                                    onChange={(date) => {
                                        setValue('dateOfBirth', date, { shouldValidate: true });
                                    }}
                                    errors={errors}
                                    placeholderText={t('common.dateFormat')}
                                />
                            </div>
                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.sex')}<span className="required">*</span></span>
                                <div className="radio-group">
                                    <div className="radio-item">
                                        <input
                                            type="radio"
                                            value="female"
                                            {...register('sex')}
                                        />
                                        <label>{t('persons.form.female')}</label>
                                    </div>
                                    <div className="radio-item">
                                        <input
                                            type="radio"
                                            value="male"
                                            {...register('sex')}
                                        />
                                        <label>{t('persons.form.male')}</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Name Section */}
                        <div className="form-group name-section">
                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.name')}<span className="required">*</span></span>
                                <input
                                    type="text"
                                    className={`form-control text-uppercase ${errors.name ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.name')}
                                    {...register('name')}
                                />
                                {errors.name && (
                                    <div className="invalid-feedback d-block">{errors.name.message}</div>
                                )}
                            </div>

                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.fathersName')}</span>
                                <input
                                    type="text"
                                    className={`form-control text-uppercase ${errors.fathersName ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.fathersName')}
                                    {...register('fathersName')}
                                />
                                {errors.fathersName && (
                                    <div className="invalid-feedback d-block">{errors.fathersName.message}</div>
                                )}
                            </div>

                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.surname')}<span className="required">*</span></span>
                                <input
                                    type="text"
                                    className={`form-control text-uppercase ${errors.surname ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.surname')}
                                    {...register('surname')}
                                />
                                {errors.surname && (
                                    <div className="invalid-feedback d-block">{errors.surname.message}</div>
                                )}
                            </div>
                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.birthplace')}<span className="required">*</span></span>
                                <div className="birthplace-inputs">
                                    <Select
                                        className="select-container"
                                        classNamePrefix="select"
                                        placeholder={t('persons.form.birthplaceCountry')}
                                        options={countryOptions}
                                        onChange={handleBirthplaceCountryChange}
                                        value={countryOptions.find(option => option.label === birthplaceCountry) || null}
                                    />
                                </div>
                                {errors.birthplaceCountry && (
                                    <div className="invalid-feedback d-block">{errors.birthplaceCountry.message}</div>
                                )}
                            </div>
                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.address')}</span>
                                <input
                                    type="text"
                                    className={`form-control ${errors.birthplace ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.address')}
                                    {...register('birthplace')}
                                />
                            </div>
                        </div>
                        {/* Identity Card Section */}
                        <div className="form-group identity-section">
                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.identityCardNo')}<span className="required">*</span></span>
                                <input
                                    type="text"
                                    className={`form-control ${errors.identityCardNo ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.identityCardNo')}
                                    {...register('identityCardNo')}
                                    maxLength={9}
                                />
                                {errors.identityCardNo && (
                                    <div className="invalid-feedback d-block">{errors.identityCardNo.message}</div>
                                )}
                            </div>

                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.identifyCardIssue')}</span>
                                <CustomDatePicker
                                    name="identityCardExpDate"
                                    value={identityCardExpDateValue}
                                    onChange={(date) => {
                                        setValue('identityCardExpDate', date, { shouldValidate: true });
                                    }}
                                    placeholderText={t('common.dateFormat')}
                                />
                            </div>

                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.identityCardPublished')}<span className="required">*</span></span>
                                <input
                                    type="text"
                                    className={`form-control ${errors.identityCardPublished ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.identityCardPublished')}
                                    {...register('identityCardPublished')}
                                />
                                {errors.identityCardPublished && (
                                    <div className="invalid-feedback d-block">{errors.identityCardPublished.message}</div>
                                )}
                            </div>
                        </div>

                        {/* Location Section */}
                        <div className="form-group location-section">
                            <LocationDropDowns
                                register={register}
                                setValue={setValue}
                                data={personData}
                                formValues={locationFormValues}
                            />
                            <input type="hidden" {...register('addressCountryId')} />
                            <input type="hidden" {...register('addressRegionId')} />
                            <input type="hidden" {...register('addressSubRegionId')} />
                            <div className="form-input-wrapper">
                                <span className='span-bold'>{t('persons.form.streetNumber')}<span className="required">*</span></span>
                                <input
                                    type="text"
                                    className={`form-control text-uppercase ${errors.address ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.streetNumber')}
                                    {...register('address')}
                                />
                                {errors.address && (
                                    <div className="invalid-feedback d-block">{errors.address.message}</div>
                                )}
                            </div>
                        </div>

                        {/* Contact Section */}
                        <div className="form-group contact-section">
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.form.phone')}</span>
                                <input
                                    type="tel"
                                    className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.phone')}
                                    {...register('phone')}
                                />
                                {errors.phone && (
                                    <div className="invalid-feedback d-block">{errors.phone.message}</div>
                                )}
                            </div>

                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.form.mobile')}</span>
                                <input
                                    type="tel"
                                    className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                                    placeholder={t('persons.form.mobile')}
                                    {...register('mobile')}
                                />
                                {errors.mobile && (
                                    <div className="invalid-feedback d-block">{errors.mobile.message}</div>
                                )}
                            </div>
                        </div>

                        {serverMessage && <div className="error-message">{serverMessage}</div>}

                        <div className="form-controls">
                            <button type="submit" className="form-btn btn-blue">
                                {t('common.save')}
                            </button>
                            <button type="button" className="form-btn btn-red" onClick={onClose}>
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
} 